import {
  type CallhomeCamera,
  type CHCameraId,
  type DeviceEntity,
  type SiteId,
  type VMSCameraId,
} from '@solinkcorp/api-types';
import CameraActions from 'actions/CameraActions';
import { ImmutableModelStore } from 'solox';
import { type CamerasDictionary, type VideoDiscoverySite } from 'utils/types';
import { type VIDEO_PLAYER_CONTEXTS } from 'vd-constants';

type CallhomeCameraLike = Pick<CallhomeCamera, 'id' | 'cameraId' | 'siteId' | 'deviceId'>;

export type CameraSpecifier = Omit<CameraIdentifier, 'playerId'>;

export type PlayingCamera = CameraIdentifier & {
  location: VideoDiscoverySite;
  camera: CallhomeCamera;
  device: DeviceEntity;
};

/** Returns true if the given cameraOrSpecifier is a callhome camera. */
function isCallhomeCamera(
  cameraOrSpecifier: CameraSpecifier | CallhomeCameraLike,
): cameraOrSpecifier is CallhomeCameraLike {
  return 'deviceId' in cameraOrSpecifier;
}

/** Returns true if the given cameraOrSpecifier is a CameraSpecifier. */
function isCameraSpecifier(
  cameraOrSpecifier: CameraSpecifier | CallhomeCameraLike,
): cameraOrSpecifier is CameraSpecifier {
  const locationKey =
    'siteId' in cameraOrSpecifier && cameraOrSpecifier.siteId !== undefined
      ? 'siteId'
      : 'locationId'; // TODO: remove after complete siteId migration
  return (
    'vmsCameraId' in cameraOrSpecifier &&
    'chCameraId' in cameraOrSpecifier &&
    locationKey in cameraOrSpecifier
  );
}

export function callhomeCameraToCameraIdentifier(camera: CallhomeCameraLike): CameraIdentifier {
  return {
    locationId: camera.siteId,
    chCameraId: camera.id,
    vmsCameraId: camera.cameraId,
    playerId: `${camera.cameraId}-${camera.deviceId}`,
  };
}

/**
 * Convert a CameraSpecifier or a CallhomeCamera into a CameraIdentifier to store in the reducer.
 */
function normalize(cameraOrSpecifier: CameraSpecifier | CallhomeCameraLike): CameraIdentifier {
  if (cameraOrSpecifier && isCallhomeCamera(cameraOrSpecifier)) {
    // This is the case where someone passed in a `CallhomeCameraLike`.
    return callhomeCameraToCameraIdentifier(cameraOrSpecifier);
  } else if (cameraOrSpecifier && isCameraSpecifier(cameraOrSpecifier)) {
    // This is the case where someone passed in a `CameraSpecifier`.
    const hasSiteId = 'siteId' in cameraOrSpecifier && cameraOrSpecifier.siteId !== undefined;
    const locationSpecifierId = hasSiteId
      ? cameraOrSpecifier.siteId!
      : cameraOrSpecifier.locationId; // TODO: remove after complete siteId migration

    return {
      locationId: locationSpecifierId,
      chCameraId: cameraOrSpecifier.chCameraId,
      vmsCameraId: cameraOrSpecifier.vmsCameraId,
      playerId: `${cameraOrSpecifier.vmsCameraId}-${cameraOrSpecifier.locationId}`,
    };
  }
  throw new Error(`Invalid camera identifier: ${JSON.stringify(cameraOrSpecifier)}`);
}

function getPlayingCameras(
  locationsById: { [locationId: SiteId]: VideoDiscoverySite } | undefined,
  camerasById: CamerasDictionary | undefined,
  playingCameraIds: readonly CameraIdentifier[],
  devicesById: Record<string, DeviceEntity> | undefined,
): PlayingCamera[] {
  return playingCameraIds.flatMap((entry) => {
    const location = locationsById?.[entry.locationId!];
    const camera = camerasById?.[entry.chCameraId];
    const device = devicesById?.[camera?.deviceId ?? ''];

    if (!location || !camera || !device) {
      return [];
    }
    return [{ ...entry, location: { ...location }, camera: { ...camera }, device: { ...device } }];
  });
}

export type CameraIdentifier = {
  locationId: SiteId; // TODO: remove after complete siteId migration
  siteId?: SiteId;
  /**
   * This is the CallHome camera ID (i.e. camera.id).
   * This is unique globally.
   */
  chCameraId: CHCameraId;
  /**
   * This is the VMS camera ID (i.e. camera.cameraId).
   * This is only unique with respect to this location.
   */
  vmsCameraId: VMSCameraId;
  /**
   * This is `${vmsCameraId}-${locationId}` - it's here for convenience, because
   * this is used throughout our product.
   */
  playerId: string;
};

export interface PlayingCamerasState {
  playingCameraIds: CameraIdentifier[];
  playingCameras: PlayingCamera[];
  initialVideoPlayerContext?: keyof typeof VIDEO_PLAYER_CONTEXTS;
  highlightedCameraId?: string;
  /** camera with enabled audio in multiplay. Format `${vmsCameraId}-${locationId}` */
  audioEnabledPlayerId?: string;
}

export class PlayingCamerasController {
  public state: ImmutableModelStore<PlayingCamerasState>;
  private authenticated: boolean;

  constructor(authenticated: boolean) {
    this.authenticated = authenticated;
    this.state = new ImmutableModelStore<PlayingCamerasState>({
      playingCameraIds: [],
      playingCameras: [],
      highlightedCameraId: undefined,
      initialVideoPlayerContext: undefined,
      audioEnabledPlayerId: undefined,
    });
  }

  setPlayingCamera(
    camera: CameraSpecifier | CallhomeCameraLike,
    options: {
      initialVideoPlayerContext?: keyof typeof VIDEO_PLAYER_CONTEXTS;
    } = {},
  ) {
    this.setPlayingCameras([camera], options);
  }

  setPlayingCameras(
    cameras: (CameraSpecifier | CallhomeCameraLike)[],
    options: {
      initialVideoPlayerContext?: keyof typeof VIDEO_PLAYER_CONTEXTS;
    } = {},
  ) {
    const cameraIdentifiers = cameras.map(normalize);

    // Ugly hack - call into old camera actions, because the recently-viewed
    // camera group is handled in the old flux code.

    if (this.authenticated) {
      for (const camera of cameraIdentifiers) {
        CameraActions.addToRecentlyViewed({ id: camera.chCameraId });
      }
    }

    this.state.update((draft) => {
      draft.playingCameraIds = cameraIdentifiers.slice();
      draft.initialVideoPlayerContext = options.initialVideoPlayerContext;
    });
  }

  addPlayingCamera(camera: CameraSpecifier | CallhomeCameraLike) {
    const cameraIdentifier = normalize(camera);

    // Ugly hack - call into old camera actions, because the recently-viewed
    // camera group is handled in the old flux code.
    if (this.authenticated) {
      CameraActions.addToRecentlyViewed({ id: cameraIdentifier.chCameraId });
    }

    this.state.update((draft) => {
      const existingEntry = draft.playingCameraIds.find(
        (entry) => entry.chCameraId === cameraIdentifier.chCameraId,
      );
      if (!existingEntry) {
        draft.playingCameraIds.push(cameraIdentifier);
      }
    });
  }

  removePlayingCamera(chCameraId: string | CameraSpecifier | CallhomeCameraLike) {
    let chCameraIdNormalized: string;

    if (typeof chCameraId === 'string') {
      chCameraIdNormalized = chCameraId;
    } else {
      const cameraIdentitifer = normalize(chCameraId);
      chCameraIdNormalized = cameraIdentitifer.chCameraId;
    }

    this.state.update((draft) => {
      draft.playingCameraIds = draft.playingCameraIds.filter(
        (camera) => camera.chCameraId !== chCameraIdNormalized,
      );
    });
  }

  clearPlayingCameras() {
    this.state.update((draft) => {
      draft.playingCameraIds = [];
    });
  }

  setHighlightCameraId(cameraId: string | undefined) {
    this.state.update((draft) => {
      draft.highlightedCameraId = cameraId;
    });
  }

  syncPlayingCameras(
    locationsById:
      | {
          [locationId: SiteId]: VideoDiscoverySite;
        }
      | undefined,
    camerasById: CamerasDictionary | undefined,
    playingCameraIds: readonly CameraIdentifier[],
    devicesById: Record<string, DeviceEntity> | undefined,
  ) {
    this.state.update((draft) => {
      draft.playingCameras = getPlayingCameras(
        locationsById,
        camerasById,
        playingCameraIds,
        devicesById,
      );
    });
  }

  getPlayingCameras() {
    return this.state.current.playingCameras as PlayingCamera[];
  }

  /** Clears the audioEnabledPlayerId if the playing camera's playerId matches playerId in state */
  updateAudioEnabledPlayerId(playingCamera: PlayingCamera) {
    if (playingCamera.playerId === this.state.current.audioEnabledPlayerId) {
      this.clearAudioEnabledPlayerId();
    } else {
      this.setAudioEnabledPlayerId(playingCamera.playerId);
    }
  }

  private setAudioEnabledPlayerId(playerId: string) {
    this.state.update((draft) => {
      draft.audioEnabledPlayerId = playerId;
    });
  }

  isAudioEnabledByPlayingCamera(playingCamera: PlayingCamera) {
    return this.state.current.audioEnabledPlayerId === playingCamera.playerId;
  }

  clearAudioEnabledPlayerId() {
    this.state.update((draft) => {
      draft.audioEnabledPlayerId = undefined;
    });
  }
}
